import * as Highcharts from 'highcharts'
import { HighchartsReact } from 'highcharts-react-official'
import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { useUserState } from 'src/hooks/userState'
import ErrorIcon from 'src/imgs/error_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import Select from 'src/views/components/Select'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function LecturerCourseSurveyResults(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('subPage:survey_results')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const [searchParams] = useSearchParams()
  const headers = useAuthenticatedHeaders()
  const userState = useUserState()

  const t = useTranslatable()
  const surveyId = searchParams.get('surveyId')
  const params = useParams()
  const theme = useTheme()
  const isDarkMode = theme === 'dark'
  const {
    data,
    isLoading: isSurveysLoading,
    error,
  } = useApi({
    endpoint: Api.getLecturerCoursesSurveyChartData,
    params: React.useMemo(
      () => ({
        headers,
        args: {
          id: params.courseId!,
          surveyId: surveyId!,
        },
      }),
      [headers, params.courseId, surveyId]
    ),
    shouldFetch: surveyId != null,
    suspense: false,
    loadingTimeout: 120000,
  })

  if (userState.data?.regime.allowLecturerSurvey !== 'ALLOWED') {
    return null
  }

  return (
    <div className="flex w-full flex-col items-center">
      <Surveys isSurveysLoading={isSurveysLoading} />
      {error ? (
        <NoContent header={t('error:an_error_occurred')} image={ErrorIcon} />
      ) : isSurveysLoading ? (
        <Loader className="m-auto flex" style={{ margin: 'auto' }} />
      ) : Array.isArray(data) ? (
        data.map((item, i) => <Charts key={i} data={item} isDarkMode={isDarkMode} />)
      ) : null}
    </div>
  )
}

interface SurveyProps {
  isSurveysLoading: boolean
}

function Surveys({ isSurveysLoading }: SurveyProps): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSemester = useActiveSemester()
  const semIdParam = searchParams.get('semId')
  const surveyIdParam = searchParams.get('surveyId')
  const semId = semIdParam ?? activeSemester?.id
  const params = useParams()
  const t = useTranslatable()
  const { data: surveyList } = useApi({
    endpoint: Api.getLecturerCoursesSurveyResults,
    params: React.useMemo(
      () => ({
        headers,
        args: {
          id: params.courseId!,
          semId: semId!,
        },
      }),
      [headers, params.courseId, semId]
    ),
    shouldFetch: semId != null,
  })

  const surveyListOptions = surveyList?.map((survey) => ({
    value: survey.id,
    label: survey.title,
  }))

  return (
    <Select
      className="mb-5 w-full"
      value={surveyListOptions?.filter((option) => option.value === surveyIdParam) || undefined}
      options={surveyListOptions}
      onChange={(option) =>
        setSearchParams((params) => {
          if (option) {
            params.set('surveyId', option.value)
          } else {
            params.delete('surveyId')
          }
          return params
        })
      }
      placeholder={t('lecturerSurveyResults:choose_survey')}
      isDisabled={isSurveysLoading}
      isLoading={isSurveysLoading}
    />
  )
}

interface ChartsProps {
  data: Api.SurveyChart
  isDarkMode?: boolean
}

function Charts({ data, isDarkMode }: ChartsProps): JSX.Element | null {
  const options: Highcharts.Options = {
    chart: {
      plotBackgroundColor: undefined,
      plotBorderWidth: undefined,
      plotShadow: false,
      type: data.chartType === 'BAR' ? 'bar' : data.chartType === 'COLUMN' ? 'column' : 'pie',
      ignoreHiddenSeries: true,
      backgroundColor: 'transparent',
      spacingBottom: 20,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
      style: {
        display: 'none',
      },
    },
    yAxis: {
      title: {
        text: data.chartAxisTitle,
        style: {
          color: isDarkMode ? '#ffffff' : '#000000',
        },
      },
      max: data.countType === 'PERCENT' ? 100 : undefined,

      ...(isDarkMode && {
        labels: {
          style: {
            color: '#cccccc',
          },
        },
        gridLineColor: '#444444',
      }),
    },

    xAxis: {
      type: 'category',
      title: undefined,
      categories: [...(data.categories ?? [])],
      ...(isDarkMode && {
        labels: {
          style: {
            color: '#cccccc',
          },
        },
        gridLineColor: '#444444',
      }),
    },

    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f} %',
          style: {
            color: isDarkMode ? '#ffffff' : '#000000',
          },
        },
        showInLegend: true,
        slicedOffset: 10,
        borderColor: isDarkMode ? '#333333' : '#ffffff',
      },
      bar: {
        cursor: 'pointer',
      },
      column: {
        cursor: 'pointer',
      },
    },

    series: data.groups.map((item) => {
      if (data.chartType === 'PIE') {
        return {
          type: 'pie',
          name: item.name,
          data: Array.from(item.data),
          center: item.center
            ? [item.center[0] as string | number | null, item.center[1] as string | number | null]
            : undefined,
          size: item.size,
          visible: item.visible ?? true,
          showInLegend: true,
        }
      }
      return {
        type: data.chartType === 'BAR' ? 'bar' : 'column',
        name: item.name,
        data: Array.from(item.data),
        visible: item.visible ?? true,
      }
    }),
    legend: {
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      enabled: true,
      ...(isDarkMode && {
        itemStyle: {
          color: isDarkMode ? '#ffffff' : '#000000',
        },
        itemHoverStyle: {
          color: '#ffffff',
        },
        itemHiddenStyle: {
          color: '#cccccc',
        },
      }),
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
            plotOptions: {
              pie: {
                size: '70%',
                dataLabels: {
                  enabled: false,
                },
              },
            },
          },
        },
      ],
    },
  }
  return (
    <div className="chart flex w-full flex-col items-center rounded border-gray-400">
      <div className="flex justify-between border-0 border-b-gray-400 px-3 py-2 text-primaryTextColor">
        <span>{data.title}</span>
      </div>
      <div className="lecturer-high-chart-wrapper flex min-h-[400px] w-full max-w-[1000px] flex-auto items-center justify-center p-5">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  )
}
